import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import APDropdown from 'components/Dropdown/APDropdown';
import InputField from 'components/FormFields/InputField';
import ContactFieldType from 'components/ContactField/ContactFieldType';
import CountryWidget from 'components/FormFields/CountryWidget';

import './contact-field.scss';

const ContactField = ({
  name,
  type,
  value,
  typeValue,
  typePlaceholder,
  onBlur,
  onTypeChange,
  onDelete,
  placeholder,
}) => {
  const ref = useRef();
  const [isFocused, setIsFocused] = useState(false);

  const handleBlur = (e) => {
    onBlur(e);
  };

  const handleTypeChange = (_type) => {
    onTypeChange(_type);
  };

  const dismiss = () => {
    setIsFocused(false);
  };

  return (
    <>
      <InputField
        ref={ref}
        name={name}
        showLabel={false}
        defaultValue={value}
        placeholder={placeholder}
        onBlur={(e) => handleBlur(e)}
        onFocus={() => setIsFocused(true)}
      />
      {onDelete && (
        <DeleteButton
          onDelete={onDelete}
        />
      )}
      {(onTypeChange && isFocused) && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <ContactTypeDropdown
            fieldRef={ref}
            onChange={handleTypeChange}
            placeholder={typePlaceholder}
            type={type}
            value={typeValue}
            dismiss={dismiss}
          />
        </div>
      )}
    </>
  );
};

export const AddressContactFields = ({
  sectionTitle = 'Address',
  address,
  onAddressUpdate,
  typePlaceholder,
  autoFocus = false,
  onDelete,
  onTypeChange,
}) => {
  const ref = useRef();
  const [isFocused, setIsFocused] = useState(false);
  const typeValue = address.apLabelId === 'custom' ? address.fieldLabel : address.apLabelId;

  const { id } = address || {};

  const handleTypeChange = (type) => {
    onTypeChange(type);
  };

  const dismiss = () => {
    setIsFocused(false);
  };

  const handleDelete = () => {
    onDelete(id, 'addresses');
  };

  return (
    <div className="contact-info__address">
      <div className="lead-sidebar__row">
        <div className="lead-sidebar__label">{sectionTitle}</div>
        <div className="lead-sidebar__value">
          <InputField
            ref={ref}
            autoFocus={autoFocus}
            name="address"
            placeholder="Street Address"
            showLabel={false}
            defaultValue={address?.street || ''}
            onBlur={(e) => {
              const newValue = e.target.value.trim();
              if (newValue !== address?.street) {
                onAddressUpdate('street', newValue, e.target, id);
              }
            }}
            onFocus={() => setIsFocused(true)}
          />
        </div>
      </div>
      <div className="lead-sidebar__row">
        <div className="lead-sidebar__label">Address Line 2</div>
        <div className="lead-sidebar__value">
          <InputField
            name="address 2"
            placeholder="Line 2"
            showLabel={false}
            defaultValue={address?.extended || ''}
            onBlur={(e) => {
              const newValue = e.target.value.trim();
              if (newValue !== address?.extended) {
                onAddressUpdate('extended', newValue, e.target, id);
              }
            }}
            onFocus={() => setIsFocused(true)}
          />
        </div>
      </div>
      <div className="lead-sidebar__row">
        <div className="lead-sidebar__label">City</div>
        <div className="lead-sidebar__value">
          <InputField
            name="city"
            placeholder="City"
            showLabel={false}
            defaultValue={address?.city || ''}
            onBlur={(e) => {
              const newValue = e.target.value.trim();
              if (newValue !== address?.city) {
                onAddressUpdate('city', newValue, e.target, id);
              }
            }}
          />
        </div>
      </div>
      <div className="lead-sidebar__row">
        <div className="lead-sidebar__label">State/Province</div>
        <div className="lead-sidebar__value">
          <InputField
            name="state"
            placeholder="State/Province"
            showLabel={false}
            defaultValue={address?.region || ''}
            onBlur={(e) => {
              const newValue = e.target.value.trim();
              if (newValue !== address?.region) {
                onAddressUpdate('region', newValue, e.target, id);
              }
            }}
          />
        </div>
      </div>
      <div className="lead-sidebar__row">
        <div className="lead-sidebar__label">Postal Code</div>
        <div className="lead-sidebar__value">
          <InputField
            name="postal_code"
            placeholder="Postal Code"
            showLabel={false}
            defaultValue={address?.postcode || ''}
            onBlur={(e) => {
              const newValue = e.target.value.trim();
              if (newValue !== address?.postcode) {
                onAddressUpdate('postcode', newValue, e.target, id);
              }
            }}
          />
        </div>
      </div>
      <div className="lead-sidebar__row">
        <div className="lead-sidebar__label">Country</div>
        <div className="lead-sidebar__value">
          <CountryWidget
            id={`country-selector--${id}`}
            onChange={({ label }) => {
              if (label !== address?.country) {
                onAddressUpdate('country', label, null, id);
              }
            }}
            value={{ label: address?.country || '' }}
          />
        </div>
      </div>
      {onDelete && (
        <div className="contact-info__delete">
          <DeleteButton onDelete={handleDelete} />
        </div>
      )}
      {(onTypeChange && isFocused) && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <ContactTypeDropdown
            fieldRef={ref}
            onChange={handleTypeChange}
            placeholder={typePlaceholder}
            type="address"
            value={typeValue}
            dismiss={dismiss}
          />
        </div>
      )}
    </div>
  );
};

const DeleteButton = ({
  onDelete,
}) => {
  return (
    <button
      className="button--unstyled lead-record__delete-contact"
      onClick={onDelete}
    >
      <FontAwesomeIcon
        icon={['fas', 'trash-alt']}
      />
    </button>
  );
};

const ContactTypeDropdown = ({
  fieldRef,
  onChange,
  type,
  value,
  placeholder = 'Type',
  dismiss,
}) => {
  if (!fieldRef.current) return;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className="contact-type-selector"
    >
      <APDropdown
        isOpen
        anchorElement={fieldRef.current}
        root={null}
        dismiss={dismiss}
        overflow
        placement="right"
      >
        <ContactFieldType
          value={value}
          onChange={onChange}
          type={type}
          placeholder={placeholder}
        />
      </APDropdown>
    </div>
  );
};

export default ContactField;
